import { createApp, type App } from 'vue';
import InteractiveMap from '@/js/widgets/interactive-map/interactive-map.vue';

const MOUNT_ID = 'interactive-map-clusters';

let mapApp: App<Element> | null = null;
let unmountListenerAdded = false;
let afterEnterListenerAdded = false;

function addUnmountOnBarbaLeave(): void {
  if (unmountListenerAdded) return;
  unmountListenerAdded = true;
  window.addEventListener('stc:barba-before-leave', () => {
    unmountClustersInteractiveMap();
  });
}

function addMountAfterBarbaEnter(): void {
  if (afterEnterListenerAdded) return;
  afterEnterListenerAdded = true;
  window.addEventListener('stc:barba-after-enter', () => {
    requestAnimationFrame(() => {
      const host = document.getElementById(MOUNT_ID);
      if (!host || host.querySelector('.interactive-map')) return;
      unmountClustersInteractiveMap();
      performMount(host);
      verifyMountOrRetry();
    });
  });
}

function readClusterHubCurrentZoneId(host: HTMLElement): string {
  const scope =
    host.closest('[data-barba-namespace], .clusters-page, .clusters-hub') ??
    host.parentElement;
  const link = scope?.querySelector<HTMLElement>(
    '.clusters-list__link--current[data-zone-id]',
  );
  const fromList = link?.dataset.zoneId?.trim();
  if (fromList) return fromList;
  return document.body.getAttribute('data-current-zone-id')?.trim() ?? '';
}

function performMount(host: HTMLElement): void {
  const mapHref = host.dataset.mapHref?.trim() ?? '';
  const initialZoneId = readClusterHubCurrentZoneId(host);
  mapApp = createApp(InteractiveMap, {
    mapHref,
    initialZoneId,
    syncClusterListFocus: true,
  });
  mapApp.mount(host);
}

function verifyMountOrRetry(): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const host = document.getElementById(MOUNT_ID);
      if (!host || host.querySelector('.interactive-map')) return;
      unmountClustersInteractiveMap();
      performMount(host);
    });
  });
}

export function unmountClustersInteractiveMap(): void {
  if (mapApp) {
    mapApp.unmount();
    mapApp = null;
  }
}

export function mountClustersInteractiveMap(): void {
  addUnmountOnBarbaLeave();
  addMountAfterBarbaEnter();
  unmountClustersInteractiveMap();

  const attempt = (): boolean => {
    const el = document.getElementById(MOUNT_ID);
    if (!el) return false;
    performMount(el);
    verifyMountOrRetry();
    return true;
  };

  if (attempt()) return;

  let frames = 0;
  const retry = (): void => {
    frames += 1;
    if (attempt() || frames >= 40) return;
    requestAnimationFrame(retry);
  };
  requestAnimationFrame(retry);
}
